import * as React from 'react'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/cn'

/**
 * Which console is this?
 *
 * Two consoles share this UI — this one, watching hiring.amazon.ca, and the
 * UK port at ../amazonfastifyUK — and an operator with both open cannot tell
 * them apart: same rail, same tables, same indigo. The tab title and the
 * address bar already say which is which, and they do not help, because they
 * are things you have to READ. The fix has to be something the corner of an
 * eye can catch.
 *
 * So the rail's brand slot carries the region instead of a generic glyph:
 *
 *  - `RegionMark` is the maple leaf flag in the brand square. It assembles
 *    itself — the bands slide in, the leaf is traced and then filled — holds,
 *    comes apart again and reassembles, on a slow loop, and catches the light
 *    on hover. Its red is the only saturated red anywhere in the console, so
 *    the corner reads before any label does, and it is never confused with the
 *    accent, which is what a tinted rail or a coloured selection bar would
 *    have been.
 *  - `RegionPlate` is the mark with the wordmark, and under it "Canada" and
 *    the time in Toronto. The clock is the second signal: an operator in
 *    another zone sees a number that disagrees with their own, and the
 *    disagreement IS the identification. Eastern because that is the zone the
 *    console already keeps (`assessment-message.ts`) and where most of the
 *    sites are; the tooltip names the city so nobody mistakes it for their own.
 *
 * The UK console carries the same plate with its own flag and London time, so
 * the two corners differ in colour, shape and number at once.
 *
 * Everything is inline SVG and CSS keyframes (theme.css, "region identity"):
 * nothing to fetch, nothing on the network path, and one `setTimeout` a minute
 * for the clock.
 */

/**
 * Flag colours are the government's, deliberately not theme tokens: a flag is
 * the one thing on the screen that must not follow the palette, in either
 * theme.
 */
const RED = '#d52b1e'
const WHITE = '#ffffff'

/** The bands are 4.5 of 32 a side; the pane between them is 23 wide. */
const BAND = 4.5

/**
 * The official leaf, from the Government of Canada's flag geometry as
 * published on Wikimedia Commons, scaled uniformly into this 32-unit box so
 * that it keeps the flag's own proportion to its pane (18 wide in 23). Arcs
 * and all — it is 600 bytes, and a redrawn leaf is a leaf somebody will
 * eventually recognise as wrong.
 */
const LEAF =
  'M16.44 25.75L16.22 21.57A0.46 0.46 0 0 1 16.75 21.1L20.91 21.83L20.35 20.28A0.31 0.31 0 0 1 20.45 19.93L25 16.24L23.97 15.76A0.31 0.31 0 0 1 23.81 15.38L24.71 12.61L22.09 13.17A0.31 0.31 0 0 1 21.73 12.99L21.23 11.79L19.18 13.99A0.31 0.31 0 0 1 18.64 13.71L19.63 8.62L18.05 9.54A0.31 0.31 0 0 1 17.61 9.4L16 6.25L14.39 9.4A0.31 0.31 0 0 1 13.95 9.54L12.37 8.62L13.36 13.71A0.31 0.31 0 0 1 12.82 13.99L10.77 11.79L10.27 12.99A0.31 0.31 0 0 1 9.91 13.17L7.29 12.61L8.19 15.38A0.31 0.31 0 0 1 8.03 15.76L7 16.24L11.55 19.93A0.31 0.31 0 0 1 11.65 20.28L11.09 21.83L15.25 21.1A0.46 0.46 0 0 1 15.78 21.57L15.56 25.75Z'

/**
 * The mark alone. Decorative — every place it appears also carries the region
 * in text, visible or `sr-only` — so it is hidden from assistive technology.
 *
 * Sized inline rather than by class: it also lives inside a `Button`, whose
 * `[&_svg]:size-*` would otherwise shrink the flag to an icon.
 *
 * Two copies of the leaf path: one is the outline that gets traced on each
 * pass and then fades, the other is the fill that blooms behind it. A single path
 * wearing both a stroke and a fill would keep its stroke at rest, and a
 * stroked leaf is a bolder leaf with a fatter stem than the flag has.
 */
export function RegionMark({ size = 30, className }: { size?: number; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('ca-mark', className)}
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.27) }}
    >
      <svg viewBox="0 0 32 32" style={{ width: '100%', height: '100%' }}>
        <rect className="ca-pane" width="32" height="32" fill={WHITE} />
        <rect className="ca-band ca-band-l" width={BAND} height="32" fill={RED} />
        <rect className="ca-band ca-band-r" x={32 - BAND} width={BAND} height="32" fill={RED} />
        <path className="ca-leaf" d={LEAF} fill={RED} />
        <path className="ca-trace" d={LEAF} pathLength={1} fill="none" stroke={RED} />
      </svg>
    </span>
  )
}

/* ------------------------------------------------------------------ *
 * Toronto clock
 * ------------------------------------------------------------------ */

const TORONTO = 'America/Toronto'

/** `09:32` and `EDT` (or `EST`) — `formatToParts` so the two can be laid out separately. */
const CLOCK = new Intl.DateTimeFormat('en-CA', {
  timeZone: TORONTO,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZoneName: 'short',
})

export interface TorontoClock {
  /** `HH:MM`, 24-hour. */
  time: string
  /** `EDT` in summer, `EST` in winter. */
  zone: string
}

function readClock(): TorontoClock {
  let hour = '--'
  let minute = '--'
  let zone = ''
  for (const part of CLOCK.formatToParts(Date.now())) {
    if (part.type === 'hour') hour = part.value
    else if (part.type === 'minute') minute = part.value
    else if (part.type === 'timeZoneName') zone = part.value
  }
  return { time: `${hour}:${minute}`, zone }
}

/**
 * The time in Toronto, to the minute.
 *
 * One timer, re-armed to fire just past the next minute boundary rather than
 * every N seconds — the display cannot change between boundaries, so polling
 * between them is work for nothing. A background tab's timers are throttled
 * and its clock would drift, so the tab coming back into view re-reads and
 * re-arms. The state setter keeps the previous object when nothing changed,
 * so a re-sync on the same minute is not a render.
 */
export function useTorontoClock(): TorontoClock {
  const [clock, setClock] = React.useState(readClock)

  React.useEffect(() => {
    let timer = 0

    const update = () => {
      setClock((prev) => {
        const next = readClock()
        return next.time === prev.time && next.zone === prev.zone ? prev : next
      })
    }
    const arm = () => {
      timer = window.setTimeout(
        () => {
          update()
          arm()
        },
        60_000 - (Date.now() % 60_000) + 20
      )
    }
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return
      window.clearTimeout(timer)
      update()
      arm()
    }

    update()
    arm()
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  return clock
}

/* ------------------------------------------------------------------ *
 * Plate
 * ------------------------------------------------------------------ */

const PORTAL_HOST = 'hiring.amazon.ca'

/**
 * Mark, wordmark, region and clock, as one hover target.
 *
 * `title` is a node rather than a string because the mobile sheet has to
 * render the wordmark as a `DialogPrimitive.Title` and the rail as a plain
 * span; both pass the same classes. The tooltip is supplementary — it adds
 * the city and the portal host to what is already on screen — which is why a
 * non-focusable trigger is acceptable here.
 *
 * The zone sits on the line here, where the UK plate leaves it to the
 * tooltip: "Canada" is short enough to make room, and in a country with six
 * zones a bare time is a question, not an answer.
 */
export function RegionPlate({ title, className }: { title: React.ReactNode; className?: string }) {
  const clock = useTorontoClock()

  return (
    <Tooltip
      side="bottom"
      content={
        <>
          <span className="block font-medium">Canada console</span>
          <span className="block text-muted">
            {clock.time} {clock.zone} in Toronto · watching {PORTAL_HOST}
          </span>
        </>
      }
    >
      <div className={cn('ca-plate flex min-w-0 flex-1 items-center gap-2', className)}>
        <RegionMark />

        <span className="flex min-w-0 flex-1 flex-col">
          {title}
          <span className="ca-rise flex items-center gap-1.5 text-[12px] leading-[1.25] font-medium text-muted">
            <span className="min-w-0 truncate">Canada</span>
            <span aria-hidden="true" className="flex-none text-faint">
              ·
            </span>
            <span className="sr-only">Toronto time</span>
            {/* Mono and tabular so 09:59 → 10:00 does not shift the glyphs sideways. */}
            <time dateTime={clock.time} className="flex-none font-mono text-[11.5px] text-faint tabular">
              {clock.time} {clock.zone}
            </time>
          </span>
        </span>
      </div>
    </Tooltip>
  )
}
